import React, { useMemo, useState, useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { generateGridPoints } from '../../heatmap/lib/geolocation';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

import logger from '../../utils/logger';
// Component to handle map resize
const MapResizer = () => {
  const map = useMap();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      logger.log('[HeatmapPreviewMap] Map resized');
    }, 100);
    
    return () => clearTimeout(timer);
  }, [map]);
  
  return null;
};

// Fix for default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

// Helper function to get grid label (A1, B2, etc.)
const getGridLabel = (gridX, gridY) => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return `${letters[gridX] || gridX}${gridY + 1}`;
};

// Create ranking-based icons
const createRankingIcon = (rank, size = 24) => {
  let backgroundColor, borderColor, textColor;
  
  if (rank === null || rank === undefined) {
    // Not ranked - Gray
    backgroundColor = '#9ca3af';
    borderColor = '#6b7280';
    textColor = '#ffffff';
  } else if (rank >= 1 && rank <= 3) {
    // Rank 1-3 - Green
    backgroundColor = '#10b981';
    borderColor = '#059669';
    textColor = '#ffffff';
  } else if (rank >= 4 && rank <= 10) {
    // Rank 4-10 - Yellow
    backgroundColor = '#f59e0b';
    borderColor = '#d97706';
    textColor = '#ffffff';
  } else {
    // Rank 11+ - Red
    backgroundColor = '#ef4444';
    borderColor = '#dc2626';
    textColor = '#ffffff';
  }

  const displayText = rank ? rank.toString() : '?';
  
  return L.divIcon({
    html: `
      <div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:${backgroundColor};border:2px solid ${borderColor};
        box-shadow:0 2px 6px rgba(0,0,0,0.3);
        display:flex;align-items:center;justify-content:center;
        font-size:${size * 0.4}px;font-weight:bold;color:${textColor};
        font-family:Arial,sans-serif;
      ">${displayText}</div>
    `,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const redIcon = (() => {
  const size = 16;
  return L.divIcon({
    html: `
      <div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:#EF4444;border:2px solid #B91C1C;box-shadow:0 1px 4px rgba(0,0,0,0.25);
      "></div>
    `,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
})();

/**
 * Props:
 * - center: { lat, lng }
 * - gridSize: number (e.g., 5)
 * - radiusKm: number
 * - height: CSS height
 * - gridData: ranking data with results
 * - showRankings: boolean to show ranking-based colors
 */
const HeatmapPreviewMap = ({ center, gridSize, radiusKm, height = 360, gridData, showRankings = false }) => {
  const hasValidCenter = center && typeof center.lat === 'number' && typeof center.lng === 'number' && !Number.isNaN(center.lat) && !Number.isNaN(center.lng);
  const mapCenter = hasValidCenter ? center : { lat: 28.6139, lng: 77.2090 }; // Fallback: New Delhi, IN

  const points = useMemo(() => {
    if (showRankings && gridData?.results) {
      // Use actual grid data with rankings and businesses
      return gridData.results.map((result, index) => ({
        id: `grid-${index}`,
        lat: result.gridPoint.lat,
        lng: result.gridPoint.lng,
        rank: result.targetBusinessRank,
        gridX: result.gridPoint.gridX,
        gridY: result.gridPoint.gridY,
        businesses: result.businesses || [], // Include business data for popup
        address: result.gridPoint.address || null
      }));
    } else {
      // Generate basic grid points without rankings
      if (hasValidCenter && gridSize && radiusKm) {
        return generateGridPoints(center, radiusKm, gridSize);
      }
      
      // Fallback: Generate mock grid points around the map center for preview
      const mockGridSize = gridSize || 5;
      const mockPoints = [];
      const baseOffset = 0.01; // Small offset for visual spacing
      
      for (let x = 0; x < mockGridSize; x++) {
        for (let y = 0; y < mockGridSize; y++) {
          const offsetX = (x - Math.floor(mockGridSize / 2)) * baseOffset;
          const offsetY = (y - Math.floor(mockGridSize / 2)) * baseOffset;
          
          mockPoints.push({
            id: `mock-${x}-${y}`,
            lat: mapCenter.lat + offsetY,
            lng: mapCenter.lng + offsetX,
            gridX: x,
            gridY: y,
            isMock: true
          });
        }
      }
      
      return mockPoints;
    }
  }, [center, gridSize, radiusKm, hasValidCenter, gridData, showRankings, mapCenter]);

  return (
    <div style={{ width: '100%', height, position: 'relative', zIndex: 0 }}>
      <MapContainer 
        key={`${mapCenter.lat}-${mapCenter.lng}-${gridSize}`}
        center={[mapCenter.lat, mapCenter.lng]} 
        zoom={13} 
        style={{ width: '100%', height: '100%', borderRadius: 8 }}
        scrollWheelZoom={true}
        attributionControl={true}
        zoomControl={true}
      >
        <MapResizer />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
          minZoom={1}
        />
        {points.map((p) => (
          <Marker 
            key={p.id} 
            position={[p.lat, p.lng]} 
            icon={showRankings ? createRankingIcon(p.rank) : redIcon} 
          >
            <Popup>
              <div style={{ minWidth: '200px', fontFamily: 'Arial, sans-serif' }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '8px', color: '#1f2937' }}>
                  {showRankings && p.gridX !== undefined && p.gridY !== undefined 
                    ? `Grid Point ${getGridLabel(p.gridX, p.gridY)}`
                    : p.gridX !== undefined && p.gridY !== undefined
                    ? `Grid Point ${getGridLabel(p.gridX, p.gridY)}`
                    : 'Grid Point'
                  }
                  {p.isMock && (
                    <span style={{ 
                      marginLeft: '6px', 
                      fontSize: '10px', 
                      color: '#9ca3af',
                      fontWeight: 'normal',
                      fontStyle: 'italic'
                    }}>
                      (Preview)
                    </span>
                  )}
                </div>
                
                {showRankings && p.rank && (
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    <span style={{ 
                      backgroundColor: p.rank <= 3 ? '#10b981' : p.rank <= 10 ? '#f59e0b' : '#ef4444',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      Rank #{p.rank}
                    </span>
                  </div>
                )}
                
                {!showRankings || !p.rank ? (
                  <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                    <span style={{ 
                      backgroundColor: '#9ca3af',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      Not Ranked
                    </span>
                  </div>
                ) : null}
                
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>📍 Coordinates:</strong>
                  </div>
                  <div style={{ marginBottom: '2px' }}>
                    Lat: {p.lat.toFixed(6)}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    Lng: {p.lng.toFixed(6)}
                  </div>
                  
                  <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                    Click to copy coordinates
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default HeatmapPreviewMap;
